import { makeAutoObservable, runInAction } from 'mobx';
import { base_url } from './base_values';

const ACCESS_TOKEN_KEY = 'ipanel_access_token';
const REFRESH_TOKEN_KEY = 'ipanel_refresh_token';

class PageStore {
  lang = 'RU';
  token = null;
  refreshToken = null;
  authReady = false;
  refreshingPromise = null;
  refreshTimer = null;
  name = null;
  user_id = null;
  username = null;
  description = null;
  phone = null;
  role_name = '';
  role_id = null;
  see_all_panels = null;
  add_panels = null;
  update_firmware = null;
  see_all_users = null;
  add_users = null;
  edit_themes = null;
  get_errors = null;
  add_roles = null;
  only_myself = null;
  only_my_panel = null;
  customer_type = '';
  analytics = null;
  monitoring_view = null;
  monitoring_control = null;
  repair_console_access = null;
  incidents_manage = null;
  tickets_manage = null;
  monitoring_settings = null;
  remote_control_panel = null;

  constructor() {
    makeAutoObservable(this);
    this.token = localStorage.getItem(ACCESS_TOKEN_KEY);
    this.refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    this.scheduleRefresh();
  }

  updateLang = newLang => {
    this.lang = newLang;
  };
  updateToken = newToken => {
    this.setTokens(newToken, this.refreshToken);
  };
  updateName = newName => {
    this.name = newName;
  };
  updateUsername = newUsername => {
    this.username = newUsername;
  };
  updateRoleId = newRole => {
    this.role_id = newRole;
  };
  updateRoleName = newRole => {
    this.role_name = newRole;
  };
  updateCustomerType = newType => {
    this.customer_type = newType;
  };

  setTokens = (accessToken, refreshToken) => {
    this.token = accessToken || null;
    this.refreshToken = refreshToken || null;
    if (this.token) localStorage.setItem(ACCESS_TOKEN_KEY, this.token);
    else localStorage.removeItem(ACCESS_TOKEN_KEY);
    if (this.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, this.refreshToken);
    else localStorage.removeItem(REFRESH_TOKEN_KEY);
    this.scheduleRefresh();
  };

  tokenPayload = token => {
    try {
      const payload = token.split('.')[1];
      return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    } catch (error) {
      return null;
    }
  };

  scheduleRefresh = () => {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    if (!this.token || !this.refreshToken) return;
    const payload = this.tokenPayload(this.token);
    const expiresAt = Number(payload?.exp || 0) * 1000;
    const delay = expiresAt ? Math.max(expiresAt - Date.now() - 5 * 60 * 1000, 60 * 1000) : 6 * 60 * 60 * 1000;
    this.refreshTimer = setTimeout(() => this.refreshSession(), delay);
  };

  applyUser = result => {
    this.name = result.name;
    this.role_id = result.role_id;
    this.username = result.username;
    this.role_name = result.role_name;
    this.lang = result.lang;
    this.customer_type = result.customer_type;
    this.phone = result.phone;
    this.add_panels = result.add_panels;
    this.see_all_panels = result.see_all_panels;
    this.add_users = result.add_users;
    this.see_all_users = result.see_all_users;
    this.get_errors = result.get_errors;
    this.update_firmware = result.update_firmware;
    this.edit_themes = result.edit_themes;
    this.description = result.description;
    this.add_roles = result.add_roles;
    this.user_id = result.user_id;
    this.only_myself = result.only_myself;
    this.only_my_panel = result.only_my_panel;
    this.analytics = result.analytics;
    this.monitoring_view = result.monitoring_view;
    this.monitoring_control = result.monitoring_control;
    this.repair_console_access = result.repair_console_access;
    this.incidents_manage = result.incidents_manage;
    this.tickets_manage = result.tickets_manage;
    this.monitoring_settings = result.monitoring_settings;
    this.remote_control_panel = result.remote_control_panel;
  };

  authorize = async values => {
    const response = await fetch(base_url + 'api/users/login', {
      method: 'POST',
      body: JSON.stringify(values),
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result.access_token) {
      throw new Error(result.detail || 'Не удалось войти');
    }
    this.setTokens(result.access_token, result.refresh_token);
    return this.token;
  };

  refreshSession = async () => {
    if (!this.refreshToken) return null;
    if (this.refreshingPromise) return this.refreshingPromise;

    this.refreshingPromise = fetch(base_url + 'api/users/refresh', {
      method: 'POST',
      headers: {
        accept: 'application/json',
        Authorization: `Bearer ${this.refreshToken}`,
      },
    })
      .then(async response => {
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result.access_token) throw new Error(result.detail || 'refresh failed');
        runInAction(() => this.setTokens(result.access_token, result.refresh_token || this.refreshToken));
        return this.token;
      })
      .catch(error => {
        runInAction(() => this.unlogin());
        return null;
      })
      .finally(() => {
        runInAction(() => {
          this.refreshingPromise = null;
        });
      });

    return this.refreshingPromise;
  };

  initializeSession = async () => {
    try {
      if (!this.token && this.refreshToken) {
        await this.refreshSession();
      }
      if (this.token) {
        const ok = await this.getMe();
        if (!ok && this.refreshToken) {
          const refreshed = await this.refreshSession();
          if (refreshed) await this.getMe();
        }
      }
    } finally {
      this.authReady = true;
    }
    return !!this.token;
  };

  getMe = async () => {
    if (!this.token) return false;
    const response = await fetch(base_url + 'api/users/user/me', {
      method: 'GET',
      headers: {
        accept: 'application/json',
        Authorization: `Bearer ${this.token}`,
      },
    });
    if (response.status === 401 && this.refreshToken) {
      const refreshed = await this.refreshSession();
      if (refreshed) return this.getMe();
    }
    if (response.status === 200) {
      const result = await response.json();
      this.applyUser(result);
      return true;
    }
    return false;
  };

  authenticatedFetch = async (url, options = {}) => {
    const headers = { ...(options.headers || {}) };
    if (this.token) headers.Authorization = `Bearer ${this.token}`;
    let response = await fetch(url, { ...options, headers });
    if (response.status === 401 && this.refreshToken) {
      const refreshed = await this.refreshSession();
      if (refreshed) {
        response = await fetch(url, {
          ...options,
          headers: { ...(options.headers || {}), Authorization: `Bearer ${this.token}` },
        });
      }
    }
    return response;
  };

  logout = async () => {
    const refresh = this.refreshToken;
    this.unlogin();
    if (refresh) {
      await fetch(base_url + 'api/users/logout', { method: 'POST' }).catch(() => null);
    }
  };

  unlogin = () => {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    this.setTokens(null, null);
    this.name = null;
    this.role_id = null;
    this.username = null;
    this.role_name = '';
    this.lang = 'RU';
    this.customer_type = '';
    this.phone = null;
    this.add_panels = null;
    this.see_all_panels = null;
    this.add_users = null;
    this.see_all_users = null;
    this.get_errors = null;
    this.update_firmware = null;
    this.edit_themes = null;
    this.description = null;
    this.add_roles = null;
    this.user_id = null;
    this.only_myself = null;
    this.only_my_panel = null;
    this.analytics = null;
    this.monitoring_view = null;
    this.monitoring_control = null;
    this.repair_console_access = null;
    this.incidents_manage = null;
    this.tickets_manage = null;
    this.monitoring_settings = null;
    this.remote_control_panel = null;
  };
}

export default PageStore;
